import type { AssessmentMonth } from '@lmc/ts-common'
import { Text } from '@lmc/ui-kit/src/general'
import { Tooltip } from 'antd'
import dayjs from 'dayjs'
import { NAMESPACE } from 'locale'
import { riskAssessmentReportConstants as translations } from 'locale/riskAssessmentReports'
import _ from 'lodash'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import styled from 'styled-components'

interface RiskAssessmentCellProps {
  row: AssessmentMonth[]
  month: string
}

const getAssessmentUrl = (resident: string, id: string) =>
  `/residents/${resident}/risk-assessments/${id}`

export const RiskAssessmentCell = ({ row, month }: RiskAssessmentCellProps) => {
  const { t } = useTranslation(NAMESPACE.RISK_ASSESSMENT_REPORT)

  const data = _.find(row, { month })
  if (!data?.assessments?.length) return <Container />

  return (
    <Container>
      {data?.assessments?.map(
        ({ riskLevel, resident, id, reviewedOn, createdByData, riskScore }) => (
          <Link
            key={id}
            target="_blank"
            rel="noopener noreferrer"
            to={getAssessmentUrl(resident, id)}
          >
            <Tooltip
              title={
                <TooltipContent>
                  <TooltipTitleText weight="bold">
                    {riskLevel
                      ? `${_.capitalize(riskLevel)} ${t(translations.RISK)}`
                      : t(translations.TOOLTIP_ASSESSMENT_COMPLETE)}
                  </TooltipTitleText>
                  <TooltipInfoText>
                    {t(translations.TOOLTIP_REVIEWED_ON, {
                      date: dayjs(reviewedOn)?.format('DD-MM-YYYY')
                    })}
                  </TooltipInfoText>
                  <TooltipInfoText>
                    {t(translations.TOOLTIP_REVIEWED_BY, {
                      name: `${createdByData?.name?.first} ${createdByData?.name?.last}`
                    })}
                  </TooltipInfoText>
                </TooltipContent>
              }
            >
              <RiskComponent riskLevel={riskLevel}>
                {riskScore?.toFixed() || t(translations.RA_SHORT)}
              </RiskComponent>
            </Tooltip>
          </Link>
        )
      )}
    </Container>
  )
}

const Container = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  width: 76px;
`

const TooltipContent = styled.div`
  padding: 10px;
  display: flex;
  flex-direction: column;
  gap: 10px;
`

const TooltipTitleText = styled(Text)`
  color: ${({ theme }) => theme.color.neutral.color3};
`

const TooltipInfoText = styled(Text)`
  color: ${({ theme }) => theme.color.neutral.color5};
`

const RiskComponent = styled.div<{ riskLevel: string | undefined }>`
  border-radius: 8px;
  font-size: 14px;
  font-weight: bold;
  color: ${({ theme }) => theme.color.neutral.color11};
  text-align: center;
  padding-top: 6px;
  flex: 1;
  max-width: 34px;
  width: 34px;
  height: 34px;
  background-color: ${({ riskLevel, theme }) =>
    riskLevel
      ? {
          severe: '#d3002c',
          high: theme.color.category.incident,
          medium: theme.color.category.activitiesAndSocial
        }[riskLevel?.toLowerCase()] || theme.color.category.personalCare
      : theme.color.neutral.color5};
`

export default RiskAssessmentCell
